import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Loader2, ArrowLeft, ArrowRight, Check, Sparkles, HandHeart, X } from 'lucide-react';
import { api } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useWizardState } from './wizard/useWizardState';
import { StepCategory, StepStory, StepGoal, StepBeneficiary, StepReview, DEFAULT_IMAGES } from './wizard/steps';
import { StepContacts } from './wizard/StepContacts';

const TOTAL_STEPS = 6;
const isDev = process.env.NODE_ENV === 'development';

function WizardHeader({ type, step, accent, onClose }) {
  return (
    <div className="px-6 pt-6 pb-4 border-b border-black/5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl text-white flex items-center justify-center" style={{ background: accent }}>
            {type === 'Yako' ? <HandHeart className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
          </div>
          <div>
            <div className="text-[11px] tracking-[0.16em] uppercase font-semibold" style={{ color: accent }}>
              Nouvelle cagnotte {type}
            </div>
            <div className="text-[15.5px] font-semibold text-[#1a1d1a]">Étape {step} sur {TOTAL_STEPS}</div>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5" aria-label="Fermer">
          <X className="w-4 h-4" />
        </button>
      </div>
      <Progress value={(step / TOTAL_STEPS) * 100} className="mt-4 h-1.5" />
    </div>
  );
}

export default function CreateCagnotteWizard({ open, onOpenChange, initialType = 'Joyeuse', onCreated }) {
  const { state, setValue, next, prev, reset, canNext } = useWizardState(initialType);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const {
    step, type, category, title, description, image,
    goal, duration, country, provider, beneficiaryPhone, beneficiaryHolder,
    contacts, channels,
  } = state;

  const accent = type === 'Yako' ? '#5a8268' : '#d97757';
  const accentBg = type === 'Yako' ? 'bg-[#5a8268]' : 'bg-[#d97757]';
  const accentHover = type === 'Yako' ? 'hover:bg-[#4a6f57]' : 'hover:bg-[#c66745]';

  useEffect(() => {
    if (open) reset(initialType);
  }, [open, initialType, reset]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/cagnottes', {
        type,
        category,
        title: title.trim(),
        description: description.trim(),
        image: image || DEFAULT_IMAGES[type],
        goal: Number(goal),
        durationDays: Number(duration),
        country,
        beneficiary: { provider, phone: beneficiaryPhone.trim(), holder: beneficiaryHolder.trim() || undefined },
      });
      const created = data.cagnotte;

      // Fire-and-forget invitations if the organiser added contacts
      if (created?.id && contacts?.length && channels?.length) {
        try {
          const res = await api.post(`/cagnottes/${created.id}/invite`, { contacts, channels });
          toast({
            title: 'Cagnotte publiée !',
            description: `${res.data?.count || contacts.length} invitation(s) enregistrée(s) (mode démo, aucun envoi réel pour l'instant).`,
          });
        } catch (err) {
          if (isDev) console.warn('[wizard] invitations échouées', err);
          toast({ title: 'Cagnotte publiée !', description: 'Cagnotte créée (invitations non enregistrées).' });
        }
      } else {
        toast({ title: 'Cagnotte publiée !', description: 'Votre cagnotte est maintenant visible.' });
      }

      if (onCreated) onCreated(created);
      onOpenChange(false);
      if (created?.id) navigate(`/cagnotte/${created.id}`);
    } catch (err) {
      if (isDev) console.warn('[wizard] création échouée', err);
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de créer la cagnotte.' });
    } finally { setSubmitting(false); }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <StepCategory type={type} setType={(v) => setValue('type', v)} category={category} setCategory={(v) => setValue('category', v)} accent={accent} />;
      case 2:
        return <StepStory type={type} title={title} setTitle={(v) => setValue('title', v)} description={description} setDescription={(v) => setValue('description', v)} image={image} setImage={(v) => setValue('image', v)} />;
      case 3:
        return <StepGoal goal={goal} setGoal={(v) => setValue('goal', v)} duration={duration} setDuration={(v) => setValue('duration', v)} country={country} setCountry={(v) => setValue('country', v)} accent={accent} />;
      case 4:
        return <StepBeneficiary provider={provider} setProvider={(v) => setValue('provider', v)} beneficiaryPhone={beneficiaryPhone} setBeneficiaryPhone={(v) => setValue('beneficiaryPhone', v)} beneficiaryHolder={beneficiaryHolder} setBeneficiaryHolder={(v) => setValue('beneficiaryHolder', v)} />;
      case 5:
        return <StepContacts contacts={contacts} setContacts={(v) => setValue('contacts', v)} channels={channels} setChannels={(v) => setValue('channels', v)} />;
      case 6:
        return <StepReview type={type} category={category} country={country} title={title} description={description} image={image} goal={goal} duration={duration} provider={provider} beneficiaryPhone={beneficiaryPhone} accent={accent} />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] p-0 overflow-hidden max-h-[92vh] flex flex-col">
        <WizardHeader type={type} step={step} accent={accent} onClose={() => onOpenChange(false)} />

        <div className="px-6 py-6 overflow-y-auto">{renderStep()}</div>

        <div className="px-6 py-4 border-t border-black/5 flex items-center justify-between bg-white">
          <Button variant="ghost" onClick={prev} disabled={step === 1} className="btn-anim rounded-full h-10 px-4 text-neutral-700">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          {step < TOTAL_STEPS ? (
            <Button onClick={next} disabled={!canNext} className={`btn-anim rounded-full h-11 px-5 text-white ${accentBg} ${accentHover}`}>
              {step === 5 && (!contacts || contacts.length === 0) ? 'Passer cette étape' : 'Continuer'} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting} className={`btn-anim rounded-full h-11 px-5 text-white ${accentBg} ${accentHover}`}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Publier la cagnotte
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
